import { DateUtils } from "@grandvisionhq/utils";
import { OpenClosedStatus, OPENING_HOURS_DAYS } from "../../types/types/index.js";
const getDayName = (index) => OPENING_HOURS_DAYS[index];
const getHoursAndMinutes = (time) => time.split(time.indexOf(".") > -1 ? "." : ":").map((t) => parseInt(t));
const getSpecialOpeningHoursForDate = (specialOpeningHours, date) => specialOpeningHours?.find(
  ({ dateOpenClosed }) => new Date(dateOpenClosed).getDate() === date.getDate() && new Date(dateOpenClosed).getMonth() === date.getMonth()
);
const getRegularOpeningHoursForDay = (openingHours, day) => {
  if (day < 0 || day > 6) {
    throw new Error("parameter day should be a number equal to 0 through 6");
  }
  const dayName = getDayName(day);
  return dayName ? openingHours[dayName] : void 0;
};
const getDateFromTime = (time) => {
  const [hours, minutes] = getHoursAndMinutes(time);
  const date = /* @__PURE__ */ new Date();
  date.setHours(hours ?? 0);
  date.setMinutes(minutes ?? 0);
  return date;
};
const normalizeClosingTime = (close, referenceDate) => close === "00:00" ? DateUtils.shiftDate(referenceDate, "hour", 24) : getDateFromTime(close);
const isOpen = (hours) => {
  const now = /* @__PURE__ */ new Date();
  return hours?.some(({ open, close }) => {
    const openDate = getDateFromTime(open);
    const closeDate = normalizeClosingTime(close, openDate);
    return now.getTime() >= openDate.getTime() && now.getTime() < closeDate.getTime();
  });
};
const isClosingSoon = (hours) => {
  const now = /* @__PURE__ */ new Date();
  return hours?.some(({ close }) => {
    const closeDate = getDateFromTime(close);
    const diff = Math.floor((closeDate.getTime() - now.getTime()) / 1e3 / 60);
    return diff > 0 && diff < 60;
  });
};
const isClosed = (hours) => {
  const now = /* @__PURE__ */ new Date();
  return hours?.every(({ close }) => {
    const closeDate = normalizeClosingTime(close, getDateFromTime(close));
    return now.getTime() > closeDate.getTime();
  });
};
const getNormalOpenCloseStatusForDay = (openingHours, day) => {
  const openingHoursForDay = getRegularOpeningHoursForDay(openingHours, day);
  const open = isOpen(openingHoursForDay);
  const closingSoon = isClosingSoon(openingHoursForDay);
  const closed = isClosed(openingHoursForDay);
  if (closed) {
    return OpenClosedStatus.Closed;
  }
  if (closingSoon) {
    return OpenClosedStatus.Closing;
  }
  return open ? OpenClosedStatus.Open : OpenClosedStatus.Closed;
};
const getOpenCloseStatus = (openingHours, specialOpeningHours) => {
  const now = /* @__PURE__ */ new Date();
  const openingHoursToday = getRegularOpeningHoursForDay(openingHours, now.getDay());
  const specialOpeningHoursToday = getSpecialOpeningHoursForDate(specialOpeningHours, now);
  let open, closingSoon, closed;
  if (specialOpeningHoursToday) {
    open = specialOpeningHoursToday.openClosed === "OPEN" && isOpen(specialOpeningHoursToday.openClosedTime);
    closingSoon = isClosingSoon(specialOpeningHoursToday.openClosedTime);
    closed = !open || isClosed(specialOpeningHoursToday.openClosedTime);
  } else {
    open = isOpen(openingHoursToday);
    closingSoon = isClosingSoon(openingHoursToday);
    closed = isClosed(openingHoursToday);
  }
  if (closed) {
    return OpenClosedStatus.Closed;
  }
  if (closingSoon) {
    return OpenClosedStatus.Closing;
  }
  return open ? OpenClosedStatus.Open : OpenClosedStatus.Closed;
};
const getOpenCloseIndication = (openingHours, specialOpeningHours, timeFormatter) => {
  const today = /* @__PURE__ */ new Date();
  const tomorrow = DateUtils.shiftDate(today, "day", 1);
  const status = getOpenCloseStatus(openingHours, specialOpeningHours);
  const specialOpeningHoursToday = getSpecialOpeningHoursForDate(specialOpeningHours, today);
  const specialOpeningHoursTomorrow = getSpecialOpeningHoursForDate(specialOpeningHours, tomorrow);
  const regularOpeningHoursToday = getRegularOpeningHoursForDay(openingHours, today.getDay());
  const regularOpeningHoursTomorrow = getRegularOpeningHoursForDay(openingHours, tomorrow.getDay());
  if (status === OpenClosedStatus.Open || status === OpenClosedStatus.Closing) {
    return {
      hours: (specialOpeningHoursToday?.openClosedTime || regularOpeningHoursToday)?.map(
        ({ open, close }) => [
          timeFormatter(getDateFromTime(open)),
          timeFormatter(getDateFromTime(close))
        ]
      )
    };
  }
  if (status === OpenClosedStatus.Closed) {
    let nextHours;
    if (specialOpeningHoursToday?.openClosed === OpenClosedStatus.Open || regularOpeningHoursToday?.length) {
      if (specialOpeningHoursToday?.openClosedTime?.length) {
        nextHours = specialOpeningHoursToday?.openClosedTime?.find(
          ({ open }) => getDateFromTime(open).getTime() > today.getTime()
        );
      } else if (regularOpeningHoursToday?.length) {
        nextHours = regularOpeningHoursToday.find(
          ({ open }) => getDateFromTime(open).getTime() > today.getTime()
        );
      }
      if (nextHours) {
        return {
          opensAt: today.getTime() === getDateFromTime("00:00").getTime() ? "TOMORROW" : "TODAY",
          hours: [[timeFormatter(getDateFromTime(nextHours.open))]]
        };
      }
    }
    if (specialOpeningHoursTomorrow?.openClosed === OpenClosedStatus.Open || regularOpeningHoursTomorrow?.length) {
      if (specialOpeningHoursTomorrow?.openClosedTime?.length) {
        nextHours = specialOpeningHoursTomorrow.openClosedTime[0];
      } else if (specialOpeningHoursTomorrow?.openClosed !== OpenClosedStatus.Closed && regularOpeningHoursTomorrow?.length) {
        nextHours = regularOpeningHoursTomorrow[0];
      }
      if (nextHours) {
        return {
          opensAt: "TOMORROW",
          hours: [[timeFormatter(getDateFromTime(nextHours.open))]]
        };
      }
    }
  }
  return {
    hours: []
  };
};
export {
  getNormalOpenCloseStatusForDay,
  getOpenCloseIndication,
  getOpenCloseStatus
};
//# sourceMappingURL=location-utils.js.map
